// SignalBeginnerGuide — collapsed-by-default beginner explainer for /signal.
//
// Plain-English overview of The Signal page and the terms operators see
// across the provider grid, candidate table, status banner, pending/
// diagnostic panels. Reuses BEGINNER_GLOSSARY so wording stays consistent.
//
// Adds a "Trading Terms Glossary" entry point that links to /glossary, so
// novices can find more term definitions without hunting through every
// section.
import { useState } from "react";
import { BookOpen, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { SectionExplainer, TermHelp } from "./BeginnerHelp";

export default function SignalBeginnerGuide() {
  const [open, setOpen] = useState(false);
  return (
    <section
      data-testid="signal-beginner-guide"
      data-state={open ? "open" : "closed"}
      className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 px-4 py-3"
    >
      <button
        type="button"
        data-testid="signal-beginner-guide-toggle"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 text-left"
        aria-expanded={open}
      >
        <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-emerald-400">
          <Sparkles className="h-4 w-4" />
          New here? Plain-English guide to The Signal
        </div>
        <span className="text-[var(--muted-text)]">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>

      {open ? (
        <div className="mt-3 space-y-3" data-testid="signal-beginner-guide-body">
          <SectionExplainer
            testId="signal-beginner-guide-overview"
            title="What is The Signal?"
            what="The Signal is the platform's research radar for stocks that pass the configured filters for a lane. It is a watchlist surface, not an order instruction."
            why="It lets an operator see which symbols have passed the day's research checks and look at the supporting context before deciding anything."
            lookAt="Use the provider grid to confirm the data feeds are working, then read the candidate rows together with the catalyst, spread, volume, and float context."
            notDo="It does NOT place trades, recommend an entry price, or rank by predicted profit. Official Signal labels and hit rate are unchanged here."
          >
            <TermHelp term="The Signal" />
            <TermHelp term="Lane Routing" />
            <TermHelp term="Candidate" />
            <TermHelp term="Catalyst" />
          </SectionExplainer>

          <SectionExplainer
            testId="signal-beginner-guide-provider-health"
            title="Reading provider health"
            what="The provider grid shows whether each data feed (quotes, news, filings, float, halts, volatility) returned recent data."
            why="If a provider is Cached, Stale, or Unavailable, downstream surfaces will mark themselves accordingly rather than guess at values."
            lookAt="Look for Live or Live-Proxy status on the providers your decision depends on. Cached and Stale are honest fallbacks, not silent errors."
            notDo="It does NOT mean trading is enabled. Live trading remains gated separately by deployment + safety state."
          >
            <TermHelp term="Provider Health" />
            <TermHelp term="Data Freshness" />
            <TermHelp term="Pending" />
            <TermHelp term="Unavailable" />
          </SectionExplainer>

          <SectionExplainer
            testId="signal-beginner-guide-candidates"
            title="Reading the candidates table"
            what="Each row is a Candidate symbol that passed the lane's filters. Spread, Volume, Float, and the lane label give context for what kind of setup it is."
            why="Wide spreads or thin volume usually mean low liquidity, which is a quality signal independent of any official label."
            lookAt="Use spread + volume together. The Watch Status and lane label tell you which research bucket the stock is in."
            notDo="No row is a buy or sell instruction. Outcome and hit-rate columns are read-only research history."
          >
            <TermHelp term="Candidate" />
            <TermHelp term="Spread" />
            <TermHelp term="Liquidity" />
            <TermHelp term="Volume" />
            <TermHelp term="Watch Status" />
          </SectionExplainer>

          <div
            className="flex flex-wrap items-center gap-3 rounded-xl border border-[var(--card-border)] bg-[var(--card-bg)] px-3 py-2 text-[12px] text-[var(--muted-text)]"
            data-testid="signal-glossary-entrypoint"
          >
            <BookOpen className="h-4 w-4 text-emerald-400" />
            <span>Want more plain-English term definitions?</span>
            <Link
              to="/glossary"
              data-testid="signal-glossary-link"
              className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[11px] font-black uppercase tracking-widest text-emerald-300 hover:bg-emerald-500/20"
            >
              Open Trading Terms Glossary
            </Link>
          </div>
        </div>
      ) : null}
    </section>
  );
}
